import { connect, ConnectedProps } from "react-redux";
import { withRouter } from "react-router";

import { isCorpTenant } from "@capone/common";
import { Dispatch } from "@reduxjs/toolkit";
import H from "history";
import {
  FareclassOptionFilter,
  IResult,
  ITrackingProperties,
  ITripTerminus,
  Lodging,
  LodgingId,
  PackagedLodging,
  PackageFetchDetailsOpaqueValue,
  PackagesViewedHotelListProperties,
  RewardsAccount,
  SliceStopCountFilter,
  TravelWalletCredit,
} from "redmond";
import { config } from "../../../../api/config";
import { IStoreState } from "../../../../reducers/types";
import {
  getRewardsAccountWithLargestEarnForBanner,
  getSelectedAccountReferenceIdIfRedemptionEnabled,
  getSelectedAccount,
  getUserIsPrimaryCardHolder,
} from "../../../rewards/reducer";
import {
  setFareclassOptionFilter as setSearchFareclassOptionFilter,
  setStopsOption as setSearchStopsOption,
} from "../../../search/actions/actions";
import {
  getDepartureDate,
  getFareclassOptionFilter,
  getOrigin,
  getReturnDate,
  getStopsOptionFilter,
} from "../../../search/reducer";
import { getTravelWalletCredit } from "../../../travel-wallet/reducer";
import {
  fetchMorePackagesAvailability,
  setFareclassOptionFilter as setAvailabilityFareclassOptionFilter,
  setStopsOption as setAvailabilityStopsOption,
  setLodgingIdHovered,
  stopFetchMorePackagesAvailability,
} from "../../actions/actions";
import {
  getFilteredAndSortedHotelAvailabilityLodgings,
  getHotelAvailabilityShowHotelIdFilter,
  getIsFilteredHotelAvailabilityLodgingsEmpty,
  getPackageAvailabilitySearchDestinationLocationResult,
  getPackageHotelQueryParams,
  getPackagesAvailabilityCallState,
  getPackagesByLodgingId,
  getPackagesViewedHotelListProperties,
  getRecommendedFlights,
  getPackageNightCountBasedOnAvailability,
  getTravelersCount,
} from "../../reducer";
import { PackagesAvailabilityCallState } from "../../reducer/state";
import { AvailabilityList } from "./component";

export enum FilterKey {
  Amenities = "Amenities",
  StarRatings = "StarRatings",
  PriceRange = "PriceRange",
  HotelName = "HotelName",
}

interface IOwnProps {}

interface IStateProps {
  returnDate: Date | null;
  departureDate: Date | null;
  nightCount: number | null;
  accountReferenceId: string | null;
  largestValueAccount: RewardsAccount;
  lodgings: Lodging[];
  packagesAvailabilityCallState: PackagesAvailabilityCallState;
  packagesByLodgingId?:
    | {
        [key: string]: PackagedLodging;
      }
    | undefined;
  searchedLocation: IResult | null;
  travelersCount: number;
  isFilteredHotelAvailabilityLodgingsEmpty: boolean;
  recommendedFlights: PackageFetchDetailsOpaqueValue;
  packageHotelQueryParams: any;
  origin: ITripTerminus | null;
  credit?: TravelWalletCredit;
  packagesViewedHotelListProperties: ITrackingProperties<PackagesViewedHotelListProperties>;
  fareClassFilter: FareclassOptionFilter;
  stopsFilter: SliceStopCountFilter;
  hotelIdFilter?: string;
  selectedRewardsAccount?: RewardsAccount;
  canEarnRewards: boolean;
}

const mapStateToProps = (state: IStoreState): IStateProps => ({
  returnDate: getReturnDate(state),
  departureDate: getDepartureDate(state),
  nightCount: getPackageNightCountBasedOnAvailability(state),
  accountReferenceId: getSelectedAccountReferenceIdIfRedemptionEnabled(state),
  largestValueAccount: getRewardsAccountWithLargestEarnForBanner(state),
  lodgings: getFilteredAndSortedHotelAvailabilityLodgings(state),
  packagesAvailabilityCallState: getPackagesAvailabilityCallState(state),
  packagesByLodgingId: getPackagesByLodgingId(state),
  searchedLocation:
    getPackageAvailabilitySearchDestinationLocationResult(state),
  travelersCount: getTravelersCount(state),
  isFilteredHotelAvailabilityLodgingsEmpty:
    getIsFilteredHotelAvailabilityLodgingsEmpty(state),
  recommendedFlights: getRecommendedFlights(state),
  packageHotelQueryParams: getPackageHotelQueryParams(state),
  origin: getOrigin(state),
  credit: getTravelWalletCredit(state),
  packagesViewedHotelListProperties:
    getPackagesViewedHotelListProperties(state),
  fareClassFilter: getFareclassOptionFilter(state),
  stopsFilter: getStopsOptionFilter(state),
  hotelIdFilter: getHotelAvailabilityShowHotelIdFilter(state),
  selectedRewardsAccount: getSelectedAccount(state),
  canEarnRewards:
    !isCorpTenant(config.TENANT) || getUserIsPrimaryCardHolder(state),
});

interface IDispatchProps {
  fetchMorePackagesAvailability: (history: H.History) => void;
  stopFetchMorePackagesAvailability: () => void;
  setLodgingIdHovered: (lodgingId: LodgingId | null) => void;
  setFareClassFilter: (fareClass: FareclassOptionFilter) => void;
  setStopsFilter: (stops: SliceStopCountFilter) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  stopFetchMorePackagesAvailability,
  fetchMorePackagesAvailability: (history: H.History) =>
    dispatch(fetchMorePackagesAvailability(history)),
  setLodgingIdHovered: (lodgingId: LodgingId | null) =>
    dispatch(setLodgingIdHovered(lodgingId)),
  setFareClassFilter: (fareClass: FareclassOptionFilter) => {
    dispatch(setAvailabilityFareclassOptionFilter(fareClass));
    dispatch(setSearchFareclassOptionFilter(fareClass));
  },
  setStopsFilter: (stops) => {
    dispatch(setAvailabilityStopsOption(stops));
    dispatch(setSearchStopsOption(stops));
  },
});

const mergeProps = (
  stateProps: IStateProps,
  dispatchProps: IDispatchProps,
  ownProps: IOwnProps,
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
});

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export type AvailabilityListConnectorProps = ConnectedProps<typeof connector>;

export const ConnectedAvailabilityList = connector(
  withRouter(AvailabilityList),
);
